// ============================================================================
// USE WORK ITEMS DATA HOOK
// Shared hook for fetching and managing work items data
// Date: October 25, 2025
// ============================================================================

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '@/lib/authService';
import { isSilentFetchError, isTransientNetworkError } from '@/lib/capacitorFetchPatch';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { useBackgroundAwareInterval } from '@/hooks/useBackgroundAwareInterval';
import { on } from '@/lib/typedEventBus';
import { WORK_ITEM_LIST_CHANGED_EVENT } from '@/lib/websocket/lifecycle';

// ============================================================================
// TYPES
// ============================================================================

export interface WorkItem {
  id: string;
  item_number: string;
  item_type: string;
  item_subtype?: string | null;
  title: string;
  description?: string;
  status: string;
  priority: 'low' | 'medium' | 'high' | 'urgent' | 'critical';
  assigned_to?: number | null;
  due_date?: string | null;
  estimated_hours?: string | number | null;
  actual_hours?: string | number | null;
  progress_percentage: number;
  parent_id?: string | null;
  children?: WorkItem[];
  created_at: string;
  updated_at: string;
  [key: string]: any; // Allow additional fields
}

type AdditionalFilterValue = string | number | boolean | Array<string | number | boolean>;

const REALTIME_REFRESH_DEBOUNCE_MS = 120;
const DEFAULT_REFRESH_INTERVAL_MS = 60_000;

// A hung request must not leave the list spinning forever — abort after this
// long, retry once, then surface an error. Server-side stalls (lock waits,
// runaway queries) currently have no timeout, so this is the only bound.
const FETCH_TIMEOUT_MS = 45_000;
const FETCH_MAX_ATTEMPTS = 2;

function parsePositiveInteger(value: unknown): number | null {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function getCurrentWorkItemsClientId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const storedClientId = parsePositiveInteger(window.localStorage.getItem('current_client_id'));
  if (storedClientId) {
    return String(storedClientId);
  }

  const userClientId = parsePositiveInteger(authService.getUser()?.client_id);
  return userClientId ? String(userClientId) : null;
}

function shouldRefreshWorkItemsFromRealtimeMessage(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const message = data as Record<string, unknown>;
  const type = typeof message.type === 'string' ? message.type : '';

  return (
    type.startsWith('appointment_')
    || type.startsWith('work_item_')
    || type === 'notification'
    || type === 'operations_refresh'
    || type === 'gpa_lifecycle_changed'
  );
}

export interface UseWorkItemsDataOptions {
  itemType?: string;
  subtype?: string;
  itemSubtype?: string; // Backward-compatible alias
  contactId?: string | number | null;
  moduleContext?: string; // Filter by module_context
  workspaceId?: string; // Filter by workspace_id (for Jira-like workspaces)
  autoLoad?: boolean;
  filterByAssignedUser?: boolean; // Filter to show only items assigned to current user
  assignedOnly?: boolean; // When true with filterByAssignedUser, excludes items only matching created_by
  assignedUserScope?: number[]; // Enforced assignee scope applied in addition to user-selected filters
  deletedFilter?: 'active' | 'deleted' | 'all'; // Controls deleted-item retrieval mode
  showDeletedOnly?: boolean; // When true, fetch only soft-deleted work items (owner/root only)
  statusFilter?: string[]; // Filter by status (e.g., ['sent', 'outstanding', 'overdue'])
  additionalFilters?: Record<string, AdditionalFilterValue>; // Additional query params to pass to the API (e.g., { source_mailbox_id: 5 })
  enableRealtimeSync?: boolean; // Subscribe to operations realtime updates with HTTP refresh fallback
  refreshIntervalMs?: number; // Polling interval used as an HTTP fallback while the view is open
  /** Full-text / API search (maps to `search` query param). */
  search?: string;
  /** 1-based page index (maps to `page`). */
  page?: number;
  /** Page size; capped at 200 server-side (maps to `limit`). */
  limit?: number;
  /** When false, API omits COUNT(*) (faster on large tables). Default true. */
  includeTotal?: boolean;
  /** Keyset pagination: fetch rows strictly after this (created_at, id); use with API default sort. */
  afterCreatedAt?: string | null;
  afterId?: string | null;
  /** On silent refresh (polling / websocket debounce), skip total count to reduce DB load. */
  silentRefreshSkipsTotal?: boolean;
  /** Filter by status array (frontend-first + backend async) */
  status?: string[];
  /** Filter by priority array (frontend-first + backend async) */
  priority?: string[];
  /** Filter by assigned user IDs (frontend-first + backend async) */
  assignedTo?: (number | null)[];
  /** Filter by tags array (frontend-first + backend async) */
  tags?: string[];
  /** Filter by date range (frontend-first + backend async) */
  dateRange?: {
    field: 'created_at' | 'updated_at' | 'due_date' | 'start_date';
    start?: Date;
    end?: Date;
  };
  /** When true, automatic refetches after the initial load happen in the background without flipping the main loading state. */
  softReloadOnParamChange?: boolean;
  /** Sort field for backend ordering (maps to sort_by query param). */
  sortBy?: string;
  /** Sort direction for backend ordering (maps to sort_order query param). */
  sortOrder?: 'asc' | 'desc';
}

export type WorkItemsDateRangeFilter = NonNullable<UseWorkItemsDataOptions['dateRange']>;

const WORK_ITEMS_DATE_RANGE_PARAM_MAP: Record<WorkItemsDateRangeFilter['field'], { after: string; before: string }> = {
  created_at: { after: 'created_after', before: 'created_before' },
  updated_at: { after: 'updated_after', before: 'updated_before' },
  due_date: { after: 'due_after', before: 'due_before' },
  start_date: { after: 'start_after', before: 'start_before' },
};

export function appendWorkItemsDateRangeParams(
  params: URLSearchParams,
  dateRange?: UseWorkItemsDataOptions['dateRange'],
) {
  if (!dateRange) {
    return;
  }

  const paramNames = WORK_ITEMS_DATE_RANGE_PARAM_MAP[dateRange.field];
  if (!paramNames) {
    return;
  }

  if (dateRange.start) {
    params.append(paramNames.after, dateRange.start.toISOString());
  }

  if (dateRange.end) {
    params.append(paramNames.before, dateRange.end.toISOString());
  }
}

export interface WorkItemsDataPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
  mode?: 'offset' | 'cursor';
  has_more?: boolean;
  next_after?: { created_at: string; id: string } | null;
}

export interface UseWorkItemsDataReturn {
  items: WorkItem[];
  loading: boolean;
  isFetching: boolean;
  error: string | null;
  loadItems: () => Promise<void>;
  refreshItems: () => Promise<void>;
  pagination: WorkItemsDataPagination | null;
}

interface FetchWorkItemsOptions {
  silent?: boolean;
  skipTotal?: boolean;
}

// ============================================================================
// HOOK
// ============================================================================

export function useWorkItemsData(options: UseWorkItemsDataOptions = {}): UseWorkItemsDataReturn {
  const {
    itemType,
    subtype,
    itemSubtype,
    contactId,
    moduleContext,
    workspaceId,
    autoLoad = true,
    filterByAssignedUser = false,
    assignedOnly = false,
    assignedUserScope,
    deletedFilter,
    showDeletedOnly = false,
    statusFilter,
    additionalFilters,
    enableRealtimeSync = false,
    refreshIntervalMs = DEFAULT_REFRESH_INTERVAL_MS,
    search,
    page = 1,
    limit = 200,
    includeTotal = true,
    afterCreatedAt = null,
    afterId = null,
    silentRefreshSkipsTotal = false,
    status,
    priority,
    assignedTo,
    tags,
    dateRange,
    softReloadOnParamChange = false,
    sortBy,
    sortOrder,
  } = options;

  const effectiveDeletedFilter: 'active' | 'deleted' | 'all' = deletedFilter || (showDeletedOnly ? 'deleted' : 'active');

  const [items, setItems] = useState<WorkItem[]>([]);
  const [pagination, setPagination] = useState<WorkItemsDataPagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [realtimeClientId, setRealtimeClientId] = useState<string | null>(() => getCurrentWorkItemsClientId());
  const hasLoadedRef = useRef(false);
  const realtimeRefreshTimeoutRef = useRef<number | null>(null);
  const lastKnownTotalRef = useRef<number | null>(null);
  const previousRequestKeyRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const inFlightRequestKeyRef = useRef<string | null>(null);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();

    if (itemType) {
      // Support comma-separated item types (e.g., "job,appointment")
      const types = itemType.split(',').map(t => t.trim()).filter(Boolean);
      types.forEach(type => params.append('item_type', type));
    }
    if (subtype || itemSubtype) params.append('item_subtype', subtype || itemSubtype || '');
    if (contactId !== undefined && contactId !== null && String(contactId).trim().length > 0) {
      params.append('contact_id', String(contactId).trim());
    }
    if (moduleContext) params.append('module_context', moduleContext);
    if (workspaceId) params.append('workspace_id', workspaceId);
    if (filterByAssignedUser) params.append('filter_by_assigned_user', 'true');
    if (assignedOnly) params.append('assigned_only', 'true');
    if (assignedUserScope && assignedUserScope.length > 0) {
      assignedUserScope.forEach((userId) => {
        if (Number.isFinite(userId)) {
          params.append('assigned_user_scope', String(userId));
        }
      });
    }
    if (effectiveDeletedFilter !== 'active') params.append('deleted_filter', effectiveDeletedFilter);
    if (statusFilter && statusFilter.length > 0) {
      // API expects status as array or comma-separated values
      statusFilter.forEach(s => params.append('status', s));
    }
    // Append any additional filters as query params
    if (additionalFilters) {
      Object.entries(additionalFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach((entry) => {
              if (entry !== undefined && entry !== null) {
                params.append(key, String(entry));
              }
            });
            return;
          }

          params.append(key, String(value));
        }
      });
    }
    // New filter parameters for frontend-first + backend async filtering
    if (status && status.length > 0) {
      status.forEach(s => params.append('status', s));
    }
    if (priority && priority.length > 0) {
      priority.forEach(p => params.append('priority', p));
    }
    if (assignedTo && assignedTo.length > 0) {
      assignedTo.forEach(id => {
        if (id !== null) {
          params.append('assigned_to', String(id));
        }
      });
    }
    if (tags && tags.length > 0) {
      tags.forEach(tag => params.append('tags', tag));
    }
    appendWorkItemsDateRangeParams(params, dateRange);
    const safeLimit = Math.min(Math.max(limit, 1), 500);
    params.append('limit', String(safeLimit));
    params.append('page', String(Math.max(page, 1)));
    const trimmedSearch = typeof search === 'string' ? search.trim() : '';
    if (trimmedSearch) {
      params.append('search', trimmedSearch);
    }
    if (!includeTotal) {
      params.append('include_total', 'false');
    }
    const trimmedAfterCa = typeof afterCreatedAt === 'string' ? afterCreatedAt.trim() : '';
    const trimmedAfterId = typeof afterId === 'string' ? afterId.trim() : '';
    if (trimmedAfterCa && trimmedAfterId) {
      params.append('after_created_at', trimmedAfterCa);
      params.append('after_id', trimmedAfterId);
      params.append('sort_by', 'created_at');
      params.append('sort_order', 'desc');
    } else if (sortBy) {
      params.append('sort_by', sortBy);
      if (sortOrder) {
        params.append('sort_order', sortOrder);
      }
    }

    return params;
  }, [
    additionalFilters,
    afterCreatedAt,
    afterId,
    assignedOnly,
    assignedUserScope,
    assignedTo,
    contactId,
    dateRange,
    effectiveDeletedFilter,
    filterByAssignedUser,
    includeTotal,
    itemType,
    itemSubtype,
    limit,
    moduleContext,
    page,
    priority,
    search,
    sortBy,
    sortOrder,
    status,
    statusFilter,
    subtype,
    tags,
    workspaceId,
  ]);

  const fetchItems = useCallback(async ({ silent = false, skipTotal = false }: FetchWorkItemsOptions = {}) => {
    for (let attempt = 0; attempt < FETCH_MAX_ATTEMPTS; attempt++) {
      const params = buildParams();
      if (skipTotal && silentRefreshSkipsTotal) {
        params.set('include_total', 'false');
      }
      const requestKey = params.toString();

      // An identical request is already in flight (mount auto-load racing a
      // realtime/manual refresh) — let it land instead of aborting and
      // reissuing the same query. An aborted controller means the in-flight
      // request is already dead (unmount/param change) even if its key ref
      // hasn't cleared yet.
      if (
        abortControllerRef.current
        && !abortControllerRef.current.signal.aborted
        && inFlightRequestKeyRef.current === requestKey
      ) {
        return;
      }

      // Abort any in-flight request so stale results (e.g. from a background
      // refresh without the search param) cannot overwrite a newer fetch.
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;
      inFlightRequestKeyRef.current = requestKey;

      if (!silent) {
        setLoading(true);
        setError(null);
      }

      setIsFetching(true);

      let timedOut = false;
      const timeoutId = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, FETCH_TIMEOUT_MS);

      try {
        const token = authService.getTokens()?.access_token;
        if (!token) {
          throw new Error('No authentication token');
        }

        const response = await fetch(`/api/v1/work-items?${requestKey}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error('Failed to load work items');
        }

        const data = await response.json();

        // Ignore results if a newer request has superseded this one.
        if (abortControllerRef.current !== controller) {
          return;
        }

        if (data.success) {
        // API returns data as array directly, not data.items
        const itemsArray = Array.isArray(data.data) ? data.data : [];
        setItems(itemsArray);
        const p = data.pagination;
        if (p && typeof p === 'object') {
          const resolvedLimit = Math.min(Math.max(limit, 1), 200);
          const rawTotal = p.total;
          const hasNumericTotal = rawTotal != null && rawTotal !== '' && Number.isFinite(Number(rawTotal));
          const pageNum = Number(p.page) || 1;
          const lim = Number(p.limit) || resolvedLimit;
          const inferredTotal = (() => {
            if (hasNumericTotal) {
              return Number(rawTotal);
            }

            const minimumVisibleTotal = Math.max(((pageNum - 1) * lim) + itemsArray.length, 0);
            const lastKnownTotal = lastKnownTotalRef.current;

            if (typeof lastKnownTotal === 'number' && Number.isFinite(lastKnownTotal)) {
              return Math.max(lastKnownTotal, minimumVisibleTotal);
            }

            return minimumVisibleTotal;
          })();

          if (Number.isFinite(inferredTotal)) {
            lastKnownTotalRef.current = inferredTotal;
          }

          const totalForUi = inferredTotal;
          const pagesFromApi = p.pages;
          const pages = hasNumericTotal && pagesFromApi != null && Number.isFinite(Number(pagesFromApi))
            ? Math.max(1, Number(pagesFromApi))
            : Math.max(1, Math.ceil(Math.max(totalForUi, 1) / lim));
          setPagination({
            page: pageNum,
            limit: lim,
            total: totalForUi,
            pages,
            mode: p.mode === 'cursor' ? 'cursor' : 'offset',
            has_more: typeof p.has_more === 'boolean' ? p.has_more : undefined,
            next_after: p.next_after && typeof p.next_after === 'object'
              ? p.next_after as { created_at: string; id: string }
              : null,
          });
        } else {
          setPagination(null);
        }
        hasLoadedRef.current = true;
      } else {
        throw new Error(data.error || 'Failed to load work items');
      }

      return;
    } catch (err: unknown) {
      // A newer fetch superseded this one — leave state management to it.
      if (abortControllerRef.current !== controller) {
        return;
      }
      // Our own timeout abort — retry once, then give up so the list stops
      // spinning instead of waiting on a hung request indefinitely.
      if (timedOut) {
        if (attempt + 1 < FETCH_MAX_ATTEMPTS) {
          continue;
        }
        console.error(`[useWorkItemsData] Work items request timed out after ${FETCH_TIMEOUT_MS}ms (attempt ${attempt + 1}/${FETCH_MAX_ATTEMPTS})`);
        if (!silent) {
          setError('Loading timed out — retrying in the background');
        }
        return;
      }
      // Aborted requests are expected when a newer fetch supersedes this one.
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      // Transient connectivity failure (server unreachable, stale socket):
      // keep the current rows and stay quiet — polling and the realtime
      // channel recover on their own. Only surface a soft error for
      // user-visible loads.
      if (isTransientNetworkError(err)) {
        if (!silent) {
          setError('Network unavailable — retrying in the background');
        }
        return;
      }
      // Suppress logging for expected background/offline rejections
      if (isSilentFetchError(err)) {
        return;
      }
      console.error('[useWorkItemsData] Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      if (!silent) {
        setItems([]);
        setPagination(null);
      }
      return;
    } finally {
      window.clearTimeout(timeoutId);
      // Only clear fetching/loading if this is still the active request. The
      // latest request owns the loading flag — a non-silent load superseded
      // by a silent refresh must still release the spinner, otherwise the
      // view stays on the loading state forever.
      if (abortControllerRef.current === controller) {
        inFlightRequestKeyRef.current = null;
        setIsFetching(false);
        setLoading(false);
      }
    }
    }
  }, [buildParams, limit, silentRefreshSkipsTotal]);

  const loadItems = useCallback(async () => {
    await fetchItems();
  }, [fetchItems]);

  const refreshItems = useCallback(async () => {
    // Cancel any pending realtime debounce so backgroundRefreshItems doesn't
    // abort this fetch 120ms later via AbortController.
    if (realtimeRefreshTimeoutRef.current) {
      window.clearTimeout(realtimeRefreshTimeoutRef.current);
      realtimeRefreshTimeoutRef.current = null;
    }
    // Silent refresh - don't set loading to true to avoid unmounting views
    // This prevents the CalendarView/Schedule view from losing its internal state
    // while still keeping pagination totals accurate for user-driven refreshes.
    await fetchItems({ silent: true, skipTotal: false });
  }, [fetchItems]);

  const backgroundRefreshItems = useCallback(async () => {
    await fetchItems({ silent: true, skipTotal: true });
  }, [fetchItems]);

  const scheduleRealtimeRefresh = useCallback(() => {
    if (!enableRealtimeSync || !autoLoad || !hasLoadedRef.current || typeof window === 'undefined') {
      return;
    }

    if (realtimeRefreshTimeoutRef.current) {
      window.clearTimeout(realtimeRefreshTimeoutRef.current);
    }

    realtimeRefreshTimeoutRef.current = window.setTimeout(() => {
      realtimeRefreshTimeoutRef.current = null;
      void backgroundRefreshItems();
    }, REALTIME_REFRESH_DEBOUNCE_MS);
  }, [autoLoad, backgroundRefreshItems, enableRealtimeSync]);

  useWebSocketChannel({
    channel: enableRealtimeSync && realtimeClientId ? `operations:client:${realtimeClientId}` : undefined,
    autoConnect: enableRealtimeSync && autoLoad,
    onMessage: (data) => {
      if (shouldRefreshWorkItemsFromRealtimeMessage(data)) {
        scheduleRealtimeRefresh();
      }
    },
  });

  // Auto-load on mount if enabled
  useEffect(() => {
    if (!autoLoad) {
      previousRequestKeyRef.current = null;
      return;
    }

    const requestKey = buildParams().toString();
    const previousKey = previousRequestKeyRef.current;
    previousRequestKeyRef.current = requestKey;

    // Params identical to the previous request — the effect only refired
    // because a callback/dep identity changed (e.g. an object prop like
    // additionalFilters={...} recreated on a parent re-render). A
    // non-silent loadItems() here would flash the loading state and
    // remount the list on every unrelated re-render; the poll + realtime
    // refreshes already keep the data fresh.
    if (hasLoadedRef.current && previousKey === requestKey) {
      return;
    }

    const shouldSoftReload = softReloadOnParamChange
      && hasLoadedRef.current
      && previousKey !== null
      && previousKey !== requestKey;

    if (shouldSoftReload) {
      refreshItems();
    } else {
      loadItems();
    }
  }, [autoLoad, buildParams, loadItems, refreshItems, softReloadOnParamChange]);

  useEffect(() => {
    if (!enableRealtimeSync || typeof window === 'undefined') {
      return;
    }

    const handleClientSwitched = () => {
      const nextClientId = getCurrentWorkItemsClientId();
      setRealtimeClientId((currentClientId) => currentClientId === nextClientId ? currentClientId : nextClientId);
      scheduleRealtimeRefresh();
    };

    window.addEventListener('client-switched', handleClientSwitched as EventListener);

    return () => {
      window.removeEventListener('client-switched', handleClientSwitched as EventListener);
    };
  }, [enableRealtimeSync, scheduleRealtimeRefresh]);

  useEffect(() => {
    if (!enableRealtimeSync || !autoLoad || typeof window === 'undefined') {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleRealtimeRefresh();
      }
    };

    const handleRefreshEvent = () => {
      scheduleRealtimeRefresh();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleRefreshEvent);
    const offWorkItemCreated = on('workItemCreated', () => handleRefreshEvent());
    const offRefreshRequested = on('workItemRefreshRequested', () => handleRefreshEvent());
    window.addEventListener('workItemStatusChange', handleRefreshEvent as EventListener);
    window.addEventListener(WORK_ITEM_LIST_CHANGED_EVENT, handleRefreshEvent as EventListener);
    window.addEventListener('work-item-sync-complete', handleRefreshEvent as EventListener);

    return () => {
      if (realtimeRefreshTimeoutRef.current) {
        window.clearTimeout(realtimeRefreshTimeoutRef.current);
        realtimeRefreshTimeoutRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleRefreshEvent);
      offWorkItemCreated();
      offRefreshRequested();
      window.removeEventListener('workItemStatusChange', handleRefreshEvent as EventListener);
      window.removeEventListener(WORK_ITEM_LIST_CHANGED_EVENT, handleRefreshEvent as EventListener);
      window.removeEventListener('work-item-sync-complete', handleRefreshEvent as EventListener);
    };
  }, [autoLoad, backgroundRefreshItems, enableRealtimeSync, refreshIntervalMs, scheduleRealtimeRefresh]);

  // Background-aware polling for work items (pauses while app is backgrounded)
  useBackgroundAwareInterval(backgroundRefreshItems, {
    intervalMs: refreshIntervalMs,
    enabled: enableRealtimeSync && autoLoad && refreshIntervalMs > 0,
    deps: [enableRealtimeSync, autoLoad, refreshIntervalMs, backgroundRefreshItems],
  });

  // Abort any in-flight request on unmount.
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    items,
    loading,
    isFetching,
    error,
    loadItems,
    refreshItems,
    pagination,
  };
}

export default useWorkItemsData;
